import { compact } from 'lodash'
import styled from 'styled-components'

import {
  ActionButton,
  ActionButtonSkeleton,
  Flex,
  Icon,
  opacity,
  RadioSelect,
  Token,
  useDropdown,
} from '@revolut-internal/ui-kit'

import { FilterPropsSingle } from './types'

const BUTTON_MIN_WIDTH = 150

const DropdownButton = styled(ActionButton)`
  background-color: ${Token.color.greyTone10};
  color: ${Token.color.foreground};
  min-width: ${BUTTON_MIN_WIDTH};

  &:hover {
    --rui-color-state-overlay: ${opacity(Token.colorChannel.black, 0.03)};
  }
  &:active {
    --rui-color-state-overlay: ${opacity(Token.colorChannel.black, 0.06)};
  }
  &[disabled],
  &:disabled,
  &[aria-disabled='true'] {
    --rui-color-state-overlay: transparent;
    color: ${opacity(Token.colorChannel.foreground, 0.3)};
    background-color: ${Token.color.greyTone10};
  }
`

export const DropdownSelect = <T extends string>({
  label,
  value,
  options = [],
  loadingState,
  disabled,
  maxHeight,
  onChange,
  renderOption,
}: FilterPropsSingle<T, T>) => {
  const dropdown = useDropdown()
  const selectedOption = options.find(option => option.value === value)
  const isExpanded = dropdown.state

  const preparedOptions = compact([
    {
      label,
      disabled: true,
      value: '' as T,
      key: 'label',
    },
    ...options,
  ])

  return (
    <>
      {loadingState === 'pending' ? (
        <ActionButtonSkeleton width={BUTTON_MIN_WIDTH} />
      ) : (
        <DropdownButton
          {...dropdown.getAnchorProps()}
          useEndIcon={
            <Icon
              name={isExpanded ? '16/ChevronUp' : '16/ChevronDown'}
              size={16}
              color={disabled ? 'grey-tone-20' : 'grey-tone-50'}
            />
          }
          aria-label={label}
          aria-haspopup="listbox"
          aria-expanded={isExpanded}
          disabled={disabled}
        >
          {selectedOption?.label ?? label}
        </DropdownButton>
      )}

      <RadioSelect
        {...dropdown.getTargetProps()}
        options={preparedOptions}
        value={value}
        labelList={`${label} list`}
        indicatorStyle="highlight"
        loadingState={loadingState}
        searchable={false}
        maxHeight={maxHeight}
        autoClose
        onChange={onChange}
      >
        {option => (
          <Flex alignItems="center" justifyContent="space-between">
            {renderOption ? renderOption(option) : option.label}
            {option.value === value && <Icon name="Check" color="accent" />}
          </Flex>
        )}
      </RadioSelect>
    </>
  )
}
